/**
 * Conditions as Foundry prepares them on an actor: which of several Conditions of one kind is
 * active, which Conditions another overrides, and the `self:condition:*` / `self:effect:*` roll
 * options every active Condition and Effect sets.
 */
import type { EngineItem } from "./items";
import type { EngineState } from "./state";

/**
 * Foundry's `ConditionPF2e#prepareSiblingData`: among Conditions of one kind only the highest
 * value is active (a locked, granted one loses to an equal unlocked one), and an active
 * Condition deactivates those its `overrides` name (blinded overrides dazzled). A deactivated
 * Condition keeps its place on the sheet but none of its Rule Elements apply.
 */
export function prepareConditionSiblings(state: EngineState): void {
  const conditions = state.items.filter((i) => i.type === "condition");
  const deactivate = (condition: EngineItem, by: EngineItem): void => {
    condition.active = false;
    if (!condition.overriddenBy.includes(by.name)) condition.overriddenBy.push(by.name);
    for (const rule of state.rules) if (rule.item === condition) rule.ignored = true;
  };
  for (const condition of conditions) {
    if (!condition.active) continue;
    const key = condition.conditionKey;
    const value = condition.badge?.value ?? null;
    const locked = !!condition.grantedBy;
    for (const other of conditions) {
      if (other === condition || !other.active || other.conditionKey !== key) continue;
      const otherValue = other.badge?.value ?? null;
      const otherIsGTE = otherValue === value || (otherValue !== null && value !== null && otherValue >= value);
      if (condition.slug === "persistent-damage") {
        // Foundry compares expected damage; without a roll, the later one yields to an equal or higher formula.
        if (expectedDamage(condition) >= expectedDamage(other)) deactivate(other, condition);
        else deactivate(condition, other);
      } else if (otherIsGTE && condition.inMemory) {
        deactivate(condition, other);
        break;
      } else if (value === otherValue && (!locked || !!other.grantedBy)) {
        deactivate(other, condition);
      } else if (!otherIsGTE) {
        deactivate(other, condition);
      }
    }
    if (!condition.active) continue;
    const overrides = Array.isArray(condition.system.overrides) ? (condition.system.overrides as unknown[]).filter((o): o is string => typeof o === "string") : [];
    for (const other of conditions) if (other !== condition && other.active && overrides.includes(other.conditionKey)) deactivate(other, condition);
  }
}

/** The average of a persistent damage formula, for ordering two of the same type (Foundry uses the roll's expected value). */
function expectedDamage(condition: EngineItem): number {
  const formula = condition.persistent?.formula ?? "";
  let total = 0;
  for (const m of formula.matchAll(/(\d+)d(\d+)/g)) total += Number(m[1]) * (Number(m[2]) + 1) / 2;
  const flat = formula.replace(/\d+d\d+/g, "").match(/[+-]?\s*\d+/g) ?? [];
  for (const f of flat) total += Number(f.replace(/\s+/g, ""));
  return total;
}

/**
 * Foundry's `AbstractEffectPF2e#prepareActorData`: every active Condition and Effect sets
 * `self:<type>:<slug>` and, with a badge, `self:<type>:<slug>:<value>` for the highest value.
 */
export function setEffectRollOptions(state: EngineState, only?: EngineItem): void {
  const items = (only ? [only] : state.items).filter((i) => i.role === "temporary" && i.active && (i.type === "condition" || i.type === "effect"));
  for (const item of items) {
    const slug = item.rollOptionSlug;
    state.rollOptions.set("all", `self:${item.type}:${slug}`);
    const value = item.badge?.value;
    if (typeof value === "number") {
      const highest = Math.max(...state.items.filter((i) => i.rollOptionSlug === slug && i.type === item.type && i.active).map((i) => i.badge?.value ?? Number.NEGATIVE_INFINITY));
      if (value >= highest) state.rollOptions.set("all", `self:${item.type}:${slug}:${value}`);
    }
    const persistent = item.persistent;
    if (persistent) state.rollOptions.set("all", `self:condition:persistent-damage:${persistent.damageType}`);
  }
}

